import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { Button } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { Note } from '@/components/Note'
import { Chip } from '@/components/Chip'
import { GridTable } from '@/components/GridTable'
import { SaveBar } from '@/components/SaveBar'
import { FIELD_CONTROL_CLASSNAME, FIELD_CONTROL_NATIVE_CLASSNAME } from '@/styles/formControls'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type CredentialHealth = components['schemas']['CredentialHealth']
type ScopeCheckRun = components['schemas']['ScopeCheckRun']

const STATUS_LABEL: Record<string, string> = { PENDING: 'Pending', VALID: 'Valid', INVALID: 'Invalid', EXPIRING_SOON: 'Expiring soon', SCOPES_REDUCED: 'Scopes reduced' }
const STATUS_CHIP: Record<string, 'pending' | 'active' | 'failure' | 'warning'> = { PENDING: 'pending', VALID: 'active', INVALID: 'failure', EXPIRING_SOON: 'warning', SCOPES_REDUCED: 'warning' }

/** `ScopeProbeState`'s own 6 members (`api/components/schemas/enums/ScopeProbeState.yaml`) — a
 * required failure and an optional failure "must not look the same" per the schema's own words;
 * the previous 3-way collapse (`PASS`/`CHECKING`/else-"Not granted") lost that distinction. */
const SCOPE_STATE_LABEL: Record<string, string> = {
  PENDING: 'Checking…',
  CHECKING: 'Checking…',
  PASS: 'Granted',
  FAIL_REQUIRED: 'Not granted',
  FAIL_OPTIONAL: 'Not granted',
  INDETERMINATE: 'Could not verify',
  NOT_APPLICABLE: 'Not applicable',
}

/**
 * `v-credential` — process/09-SCREEN-SPECS/v-credential.md. **No token value is ever rendered, in
 * any form** (rule 1) — the replace field is write-only and this screen never reads one back.
 * *Recheck scopes* re-runs `scopeCheckStart`/`scopeCheckGet` (the same start-then-poll pattern
 * `ob-wz-4` already established) — there is no "read the last run" operation, only "start one and
 * poll it," so a mount always starts a fresh check.
 *
 * **`token-card`/`field`/`field-control`/`reveal-toggle`/`tools` added W168.** The reveal toggle
 * does not violate rule 1: it only ever toggles the input type of the token the operator is
 * currently typing, never a value read back from the server — this screen still never renders a
 * stored secret in any form. There is no `i-eye-off`/`i-eye-slash` icon in this build's icon set,
 * so both toggle states reuse `i-eye` and the state is carried by the `label` text alone ("Show
 * token" / "Hide token"). `tools` now wraps the pre-existing *Recheck scopes* button in the scopes
 * panel-header, matching the spec's own "the scopes panel's control cluster" cell.
 *
 * **`link` (in `lead` — 2×) is not built.** Nothing in this spec has a `lead` paragraph at all —
 * the "What this screen is for" line is a single sentence with no link, and no other prose block
 * on this screen names a destination for two links. No confidently-correct target exists to
 * invent; reported rather than guessed at.
 *
 * **The status chip recoloured (W172)** — was `variant={status === 'VALID' ? 'default' : 'plain'}`
 * (flat blue vs. flat grey); the prototype's own `#cred-state` element is `st-active` for
 * "Healthy" and, per its own "Prototype states" scenario table, `solid-warn` for expiring/reduced
 * scopes and `solid-fail` for expired/revoked — both of which this schema's `INVALID` covers.
 * `PENDING` has no direct scenario; mapped to `pending`, the same "not yet resolved" shape every
 * other `PENDING` value in this build gets.
 *
 * **`token-card` given its own real `--gradient-card` background (W173)** — `.tokencard`'s own
 * rule is `background:linear-gradient(135deg,#FCFDFE,#F8F9FC)`, not the flat white it had. The
 * "Status" panel's own nested `meta-block` stays bare on purpose — see `OrderPane.tsx`'s own doc
 * comment for the nested-vs-top-level distinction the prototype's own CSS draws explicitly.
 */
export function VCredential() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const [token, setToken] = useState('')
  const [revealed, setRevealed] = useState(false)

  const health = useQuery({
    queryKey: ['credential-health', orgId, workspaceId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<CredentialHealth> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/credential/health', { params: { path: { org_id: orgId, workspace_id: workspaceId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as CredentialHealth
    },
  })

  const replace = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const response = await client.PUT('/orgs/{org_id}/workspaces/{workspace_id}/credential', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: { credential: token, credential_type: 'PERSONAL_API_TOKEN' },
      })
      return unwrap(response as { data?: CredentialHealth; error?: Problem })
    },
    onSuccess: () => {
      setToken('')
      queryClient.invalidateQueries({ queryKey: ['credential-health', orgId, workspaceId] })
    },
  })

  const start = useMutation({
    mutationFn: async (): Promise<ScopeCheckRun> => {
      const client = getApiClient()
      const { data, error } = await client.POST('/orgs/{org_id}/workspaces/{workspace_id}/scope-checks', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        headers: { 'Idempotency-Key': crypto.randomUUID() },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as ScopeCheckRun
    },
  })

  useEffect(() => {
    start.mutate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const runId = start.data?.run_id

  const poll = useQuery({
    queryKey: ['scope-check', runId],
    enabled: Boolean(runId),
    queryFn: async (): Promise<ScopeCheckRun> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/scope-checks/{run_id}', {
        params: { path: { org_id: orgId, workspace_id: workspaceId, run_id: runId! } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as ScopeCheckRun
    },
    refetchInterval: (query) => (query.state.data?.state === 'RUNNING' ? 750 : false),
  })

  const run = (poll.data ?? start.data) as ScopeCheckRun | undefined
  const healthProblem = health.error instanceof ApiProblemError ? health.error.problem : undefined
  const replaceProblem = replace.error instanceof ApiProblemError ? replace.error.problem : undefined

  return (
    <div data-scr={unitId('v-credential')}>
      <h1 className="m-0 mb-8 text-section font-semibold">Bitbucket credential</h1>

      {healthProblem && (isUnexpectedRefusal(healthProblem.refusal_kind) ? <UnexpectedRefusal problem={healthProblem} /> : <Note>{healthProblem.title}</Note>)}

      {health.data && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6 mb-8">
          <div data-cmp="panel-header" className="flex items-center gap-4 mb-4">
            <h3 className="m-0 text-body-lg font-semibold flex-1">Status</h3>
            <Chip variant={STATUS_CHIP[health.data.status] ?? 'plain'}>{STATUS_LABEL[health.data.status] ?? health.data.status}</Chip>
            {health.data.verified_at && (
              <span className="text-small text-tx2">
                Verified {health.data.verified_at}, by the last job — every job re-checks the credential and its scopes as its first step
              </span>
            )}
          </div>
          <div data-cmp="meta-block" className="grid gap-3" style={{ gridTemplateColumns: '160px 1fr' }}>
            <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
              Type
            </div>
            <div data-cmp="kv-value" className="text-body-lg">{health.data.credential_type ?? '—'}</div>
            <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
              Expires
            </div>
            <div data-cmp="kv-value" className="text-body-lg">{health.data.expires_at ?? 'Does not expire'}</div>
            <div data-cmp="kv-key" className="text-muted font-semibold text-body-lg">
              Last verified
            </div>
            <div data-cmp="kv-value" className="text-body-lg">{health.data.verified_at ?? 'Never'}</div>
          </div>
        </div>
      )}

      {/* Prototype's own "Configuration API calls" panel (`Prototype_v2.html` `#v-credential`) —
       * its live counter ("740 requests, this rolling hour"), 7-day rate-limit event count and
       * "typical incremental" duration are NOT built: no field on `CredentialHealth` or elsewhere
       * in the API contract carries per-workspace API-call consumption, rate-limit event history,
       * or incremental-backup duration — a genuine contract gap, confirmed against the schema
       * directly, not fabricated. The explanatory copy and the platform-wide rate ceiling below are
       * universal, static facts (never customer-specific), so they're built for real. */}
      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6 mb-8">
        <div data-cmp="panel-header" className="mb-4">
          <h3 className="m-0 text-body-lg font-semibold">Configuration API calls</h3>
        </div>
        <Note>
          Counted by us, per job and per repository, as the calls are made. Bitbucket publishes no rate-limit header of any
          kind, so there is no ceiling to show you and nothing in this product paces against a computed budget — we
          discover the limit by being refused, and back off.
        </Note>
        <div data-cmp="meta-block" className="grid gap-3 mt-4" style={{ gridTemplateColumns: '200px 1fr' }}>
          <div data-cmp="kv-key" className="text-tx2 font-semibold text-body-lg">
            Git operations ceiling
          </div>
          <div data-cmp="kv-value" className="text-body-lg">
            60,000 / hour <span className="text-tx2 text-small">(Bitbucket platform limit)</span>
          </div>
        </div>
        <p className="text-small text-tx2 mt-4">
          Your code arrives over git, which is not the constraint. Pull request and configuration history come from an API
          that refuses you once you have used too much of it in an hour, and how often that happens is what sets every
          duration in this product — which is why the two figures worth watching here are <b>how many calls a pass costs</b>{' '}
          and <b>how often it was refused</b>, never a percentage.
        </p>
      </div>

      <div data-cmp="panel" className="bg-panel border border-bd rounded-2 p-6">
        <div data-cmp="panel-header" className="flex items-center gap-4 mb-4">
          <h3 className="m-0 text-body-lg font-semibold flex-1">Scopes on this token</h3>
          <div data-cmp="tools">
            {health.data?.verified_at && <span className="text-small text-tx2 mr-3">Last probed {health.data.verified_at}</span>}
            <Button data-act={actionId('scopeCheck.start')} ghost disabled={start.isPending} onClick={() => start.mutate()}>
              Recheck scopes
            </Button>
          </div>
        </div>
        {run?.state === 'WRONG_TOKEN_KIND' ? (
          <Note>{run.terminal_message}</Note>
        ) : (
          <>
            {run?.rows && run.rows.length > 0 && (
              <p className="text-small text-tx2 mb-3">
                {run.rows.filter((r) => r.required && r.state === 'PASS').length} of {run.rows.filter((r) => r.required).length}{' '}
                required · {run.rows.filter((r) => !r.required && r.state === 'PASS').length} of{' '}
                {run.rows.filter((r) => !r.required).length} optional
              </p>
            )}
            <GridTable
              columns={[
                { key: 'scope', header: 'Scope', render: (row) => <span className="font-mono text-small">{row.scope}</span> },
                {
                  key: 'covers',
                  header: 'Covers',
                  render: (row) => (
                    <span className="text-tx2">
                      {row.label}
                      {!row.required && <Chip variant="plain">Optional</Chip>}
                    </span>
                  ),
                },
                { key: 'state', header: 'State', align: 'right', render: (row) => SCOPE_STATE_LABEL[row.state] ?? row.state },
              ]}
              rows={run?.rows ?? []}
              getRowKey={(row) => row.scope}
            />
            {run?.rows && run.rows.length > 0 && (
              <p className="text-small text-tx2 mt-4">
                {run.rows.length} probes, {run.rows.filter((r) => r.required).length} of them required. Bitbucket only
                exposes branch permissions to admin-scoped tokens. We read these; we never modify them during a backup.
              </p>
            )}
          </>
        )}
      </div>

      <div className="mt-8">
        <h3 className="m-0 mb-1 text-body-lg font-semibold">Stored credential</h3>
        <Note>The token and its health are one screen, so the five credential states render in one place.</Note>
      </div>

      <div data-cmp="token-card" className="border border-bd rounded-2 p-6 mt-4" style={{ background: 'var(--gradient-card)' }}>
        <h3 className="m-0 mb-4 text-body-lg font-semibold">Replace token</h3>
        {replaceProblem && (isUnexpectedRefusal(replaceProblem.refusal_kind) ? <UnexpectedRefusal problem={replaceProblem} /> : <Note>{replaceProblem.title}</Note>)}
        <div data-cmp="field" className="mb-3">
          <label className="block text-small font-semibold text-tx2">
            New personal API token
            <div data-cmp="field-control" className={`mt-2 ${FIELD_CONTROL_CLASSNAME}`}>
              <input
                type={revealed ? 'text' : 'password'}
                data-cmp="secret-field"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className={`${FIELD_CONTROL_NATIVE_CLASSNAME} font-mono`}
              />
              <div data-cmp="reveal-toggle">
                <IconButton
                  data-act={undefined}
                  icon="i-eye"
                  label={revealed ? 'Hide token' : 'Show token'}
                  onClick={() => setRevealed((v) => !v)}
                />
              </div>
            </div>
          </label>
        </div>
        <Note>Never shown back once saved, in any form. Must be an admin's token.</Note>
        <SaveBar dataAct={actionId('credential.replace')} saveLabel="Replace token" dirty={token.length > 0} pending={replace.isPending} onSave={() => replace.mutate()} onCancel={() => setToken('')} />
        <div className="mt-4">
          <Note>The token is stored encrypted and never written to backup storage. Reports and drift run without it.</Note>
        </div>
      </div>
    </div>
  )
}
